import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Stack, useToast } from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { reverseTransformData } from '../../util/helper';
import { createNewAdminUser, getAdminUserData, updateAdminUserData } from '../../services/AdminUsers';
import { handleApiError, showSuccessToast } from '../../util/errorHandler';

const AdminUser = () => {
    const { id } = useParams();
    const [processedData, setProcessedData] = useState({});
    const [quickSave, setQuickSave] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    const isEditMode = !!id;

    const createSchema = {
        "title": "Admin User",
        "description": "",
        "type": "object",
        "properties": {
            "username": {
                "type": "string",
                "title": "Username",
                "default": ""
            },
            "password": {
                "type": "string",
                "title": "Password",
                "default": ""
            },
            "email": {
                "type": "string",
                "title": "Email",
                "format": "email",
                "default": ""
            },
            "isActive": {
                "type": "boolean",
                "title": "Active",
                "default": true
            }
        },
        required: ['username', 'password']
    };

    const editSchema = {
        "title": "Admin User",
        "description": "",
        "type": "object",
        "properties": {
            "username": {
                "type": "string",
                "title": "Username",
                "default": ""
            },
            "password": {
                "type": "string",
                "title": "New Password",
                "default": ""
            },
            "email": {
                "type": "string",
                "title": "Email",
                "format": "email",
                "default": ""
            },
            "isActive": {
                "type": "boolean",
                "title": "Active",
                "default": true
            }
        },
        required: ['username']
    };

    const uiSchema = {
        "ui:submitButtonOptions": {
            "submitText": "Save",
            "norender": false,
            "props": {
                "disabled": false,
                "className": "button button--primary button--blue",
                "loadingText": "Saving..",
                "colorScheme": "teal"
            }
        },
        username: {
            'ui:emptyValue': ''
        },
        password: {
            'ui:widget': 'password',
            'ui:emptyValue': '',
            'ui:help': isEditMode ? 'Leave blank to keep the current password.' : undefined
        },
        email: {
            'ui:emptyValue': '',
            'ui:placeholder': 'user@example.com'
        },
        isActive: {
            'ui:widget': 'checkbox'
        }
    };

    useEffect(() => {
        const getBasicData = async () => {
            try {
                if (id) {
                    const result = await getAdminUserData(id);
                    if (result) {
                        const user = Array.isArray(result) ? result[0] : result;
                        if (user) {
                            setProcessedData({
                                username: user.username || '',
                                email: user.email || '',
                                isActive: user.isActive ?? true,
                            });
                        }
                    }
                }
            } catch (error) {
                handleApiError(error, toast, {
                    defaultErrorMessage: "Failed to fetch admin user data"
                });
            }
        };

        getBasicData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, quickSave]);

    const handleSubmit = async (formData) => {
        const updatedFormData = reverseTransformData(formData, []);

        // Strip empty password before sending to API
        if (isEditMode && (!updatedFormData.password || updatedFormData.password.trim() === '')) {
            delete updatedFormData.password;
        }

        try {
            if (isEditMode) {
                await updateAdminUserData(id, updatedFormData);
                setQuickSave(prev => !prev);
                showSuccessToast(toast, "Admin user updated successfully");
            } else {
                const result = await createNewAdminUser(updatedFormData);
                showSuccessToast(toast, "Admin user created successfully");
                navigate(`/admin-users/${result?.userId}`);
            }
        } catch (error) {
            handleApiError(error, toast, {
                defaultErrorMessage: isEditMode
                    ? "Failed to update admin user"
                    : "Failed to create admin user"
            });
        }
    };

    return (
        <Stack flexDirection={'row'} gap={"15px"} alignItems={"flex-start"}>
            <Box width={"600px"}>
                <Form
                    schema={isEditMode ? editSchema : createSchema}
                    uiSchema={uiSchema}
                    formData={processedData}
                    validator={validator}
                    onSubmit={(e) => handleSubmit(e.formData)}
                />
            </Box>
        </Stack>
    );
};

export default AdminUser;
